import enviro from 'enviro';
import { stringify } from 'hub-http/helpers/params';
import { getFullUrl } from 'hubspot-url-utils';
import { getEndOfDateTimestamp, getLogFetchDateStringForTimestamp, getNDaysAgo, getTopOfDateTimestamp, toDetailsPageDateStringFromTimestamp } from 'flow-doctor-lib/utils/dateTimeUtils';
export const getRootUrl = () => '/flow-doctor';
export const getCanvasUrl = ({
  portalId,
  flowId
}) => `${getFullUrl('app')}/workflows/${portalId}/platform/flow/${flowId}`;
export const getRevisionUrl = ({
  portalId,
  flowId,
  version,
  isEventTriggers
}) => {
  return isEventTriggers ? `${getFullUrl('app')}/workflows/${portalId}/platform/flow/${flowId}/revision/${version}/preview/edit/triggers` : `${getFullUrl('app')}/workflows/${portalId}/platform/flow/${flowId}/revision/${version}/preview/edit/actions/enrollment/filters`;
};
export const getEnrollmentHistoryLogsUrl = ({
  portalId,
  flowId,
  objectId,
  timestamp
}) => {
  const dateForTimestamp = toDetailsPageDateStringFromTimestamp(timestamp);
  return `${getFullUrl('app')}/workflows/${portalId}/details/${flowId}/enrollments?objectId=${objectId}&rangeType=CUSTOM&startDate=${dateForTimestamp}&endDate=${dateForTimestamp}`;
};
export const getContactRecordUrl = ({
  portalId,
  objectId
}) => `${getFullUrl('app')}/contacts/${portalId}/contact/${objectId}`;
export const getCompanyRecordUrl = ({
  portalId,
  objectId
}) => `${getFullUrl('app')}/contacts/${portalId}/company/${objectId}`;
export const getDealRecordUrl = ({
  portalId,
  objectId
}) => `${getFullUrl('app')}/contacts/${portalId}/deal/${objectId}`;
export const getTicketRecordUrl = ({
  portalId,
  objectId
}) => `${getFullUrl('app')}/contacts/${portalId}/ticket/${objectId}`;
export const getQuoteRecordUrl = ({
  portalId,
  objectId
}) => `${getFullUrl('app')}/quotes/${portalId}/details/${objectId}`;
export const getConversationRecordUrl = ({
  portalId,
  threadId
}) => `${getFullUrl('app')}/live-messages/${portalId}/inbox/${threadId}`;
export const getCustomObjectRecordUrl = ({
  portalId,
  objectTypeId,
  objectId
}) => `${getFullUrl('app')}/contacts/${portalId}/record/${objectTypeId}/${objectId}`;
export const getUserUrl = ({
  portalId,
  userId
}) => `${getFullUrl('app')}/settings/${portalId}/users/user/${userId}`;
const makeFrameworkDataListQueryParams = ({
  listId,
  isClassic,
  objectId
}) => Object.assign({
  listId,
  idType: isClassic ? 'CONTACTS' : 'ILS',
  propertyFetchMode: 'RELEVANT_VALUES',
  sortDirection: 'DESC',
  showMembershipChanges: true,
  showListProcessingStatus: true,
  showObjectPropertyChange: true,
  showFormSubmission: true
}, objectId && {
  objectId
});
const makeFrameworkDataEventFilterQueryParams = ({
  flowObjectType,
  objectId
}) => Object.assign({
  objectType: flowObjectType
}, objectId && {
  objectId
});
const getEnvironment = () => {
  return enviro.isProd() ? 'prod' : 'qa';
};
const makeListEvalLogFetchQueryParams = (portalId, listId, objectId, startTimestamp, endTimestamp) => {
  var _startTimestamp, _endTimestamp;
  const searchFilters = ['requestid:LIKE:InboundDbListSegKafkaEvaluator-evaluator-dynamic', `portal_id:EQUAL:${portalId}`];
  if (listId) {
    searchFilters.push(`list_id:EQUAL:${listId}`);
  }
  if (objectId) {
    searchFilters.push(`object_id:EQUAL:${objectId}`);
  }
  startTimestamp = (_startTimestamp = startTimestamp) !== null && _startTimestamp !== void 0 ? _startTimestamp : getTopOfDateTimestamp(getNDaysAgo(3, 'UTC'), 'UTC');
  endTimestamp = (_endTimestamp = endTimestamp) !== null && _endTimestamp !== void 0 ? _endTimestamp : getEndOfDateTimestamp(new Date().getTime(), 'UTC');
  return {
    serviceIds: 'ILSOBSERVABILITY.EVALUATION_PIPELINE_EVENT',
    startDate: getLogFetchDateStringForTimestamp(startTimestamp),
    endDate: getLogFetchDateStringForTimestamp(endTimestamp),
    maxResults: 10,
    environment: getEnvironment(),
    logType: 'CUSTOM_LOGFETCH_EVENT',
    searchFilters
  };
};
export const getListEvalLogFetchUrl = ({
  portalId,
  listId,
  objectId,
  startTimestamp,
  endTimestamp
}) => {
  const host = getFullUrl('private', {
    domainOverride: 'hubteam'
  });
  const queryParams = makeListEvalLogFetchQueryParams(portalId, listId, objectId, startTimestamp, endTimestamp);
  return `${host}/logfetch/search?${stringify(queryParams)}`;
};
export const getFrameworkDataListUrl = ({
  portalId,
  listId,
  isClassic,
  objectId
}) => `${getFullUrl('tools', {
  domainOverride: 'hubteam'
})}/framework-data/${portalId}/lists/membershipHistory?${stringify(makeFrameworkDataListQueryParams({
  listId,
  isClassic,
  objectId
}))}`;
export const getFrameworkDataEventFilterUrl = ({
  portalId,
  filterId,
  flowObjectType,
  objectId
}) => `${getFullUrl('tools', {
  domainOverride: 'hubteam'
})}/framework-data/${portalId}/events/pubSubFilters/${filterId}?${stringify(makeFrameworkDataEventFilterQueryParams({
  objectId,
  flowObjectType
}))}`;
export const getPumaObjectTypeUrl = ({
  objectTypeId,
  portalId
}) => {
  const queryParams = portalId ? stringify({
    portalId
  }) : '';
  return `${getFullUrl('tools', {
    domainOverride: 'hubteam'
  })}/puma/objects/${objectTypeId}/properties?${queryParams}`;
};
export const getAppUrlForRelativeUri = relativeUri => `${getFullUrl('app')}/${relativeUri}`;
export const getListUrl = ({
  portalId,
  listId
}) => {
  return `${getFullUrl('app')}/contacts/${portalId}/objectLists/${listId}/filters`;
};
export const getLogfetchUrl = queryId => `https://private-${enviro.getHublet()}.hubteam.com/logfetch/search?queryId=${queryId}`;
export const getJanusUrlForGroup = janusGroup => `https://private.hubteam.com/janus/groups/${janusGroup}`;
export const getGatingManagerGateUrl = gateName => `https://tools.hubteam.com/gates/gates/${encodeURIComponent(gateName)}/dashboard`;
export const getSlackChannelUrlForChannel = slackChannel => `https://hubspot.slack.com/archives/${slackChannel}`;
export const getEnrollmentHistoryGogglesUrl = ({
  portalId,
  flowId,
  objectTypeId,
  objectId
}) => {
  return `${getFullUrl('tools', {
    domainOverride: 'hubteam'
  })}/api/get/api-${enviro.getHublet()}.hubspot.com/automation-enrollment/v1/enrollments/flow/${flowId}/${objectTypeId}/${objectId}?authType=app&authPortal=${portalId}`;
};